/**
 * Cloudflare Turnstile, rendered explicitly.
 *
 * The widget is loaded once per page and rendered into a ref. A token is single
 * use, so the caller resets after every submit; a form is not submittable until
 * a token exists, which keeps the common failure off the server entirely.
 *
 * The site key comes from VITE_TURNSTILE_SITE_KEY and there is no fallback.
 *
 * There used to be one, to Cloudflare's published always-passes test key, so
 * that local dev worked with no configuration. The cost of that convenience is
 * that a production build with the variable unset produced a widget that
 * renders, passes every challenge, and looks exactly like a working one. The
 * auth endpoints would be sitting behind a decoration and nothing would say so.
 *
 * So: unset is a build failure, not a default. `scripts/build-site.mjs` refuses
 * to assemble the site without it, `vite.config.ts` refuses to build the portal
 * without it, and this module throws if it somehow reaches a browser without
 * one. Local dev gets the test key injected by vite.config.ts under `serve`
 * only, where it cannot be built into anything.
 */

import { useCallback, useEffect, useRef, useState, type RefObject } from 'react';

const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

/**
 * Fails the render rather than falling back. A missing key here means both
 * build-time guards were bypassed, which is worth a visible error.
 */
function siteKey(): string {
  const key = import.meta.env.VITE_TURNSTILE_SITE_KEY;
  if (!key) {
    throw new Error(
      'VITE_TURNSTILE_SITE_KEY is not set. The Turnstile widget cannot be rendered ' +
        'without it, and there is deliberately no test-key fallback. See infra/EDGE.md.',
    );
  }
  return key;
}

interface TurnstileRenderOptions {
  sitekey: string;
  callback: (token: string) => void;
  'error-callback': () => void;
  'expired-callback': () => void;
  'timeout-callback': () => void;
  theme: 'light';
  appearance: 'always';
  action: string;
}

interface TurnstileApi {
  render: (element: HTMLElement, options: TurnstileRenderOptions) => string;
  reset: (widgetId: string) => void;
  remove: (widgetId: string) => void;
}

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

let scriptPromise: Promise<void> | null = null;

function loadScript(): Promise<void> {
  if (scriptPromise) return scriptPromise;
  scriptPromise = new Promise<void>((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(`script[src="${SCRIPT_SRC}"]`);
    if (existing) {
      existing.addEventListener('load', () => resolve());
      existing.addEventListener('error', () => reject(new Error('Turnstile failed to load.')));
      return;
    }
    const script = document.createElement('script');
    script.src = SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.addEventListener('load', () => resolve());
    script.addEventListener('error', () => reject(new Error('Turnstile failed to load.')));
    document.head.appendChild(script);
  });
  return scriptPromise;
}

export interface Turnstile {
  /** Attach to the element the widget should occupy. */
  containerRef: RefObject<HTMLDivElement | null>;
  /** Present once the visitor has passed the challenge. */
  token: string | null;
  /**
   * True when the widget could not load or could not render: the script
   * failed, render threw, or Turnstile reported an error code. The form
   * should say so rather than sit silently disabled. A production example:
   * an invalid site key produces Turnstile error 400020, no widget, and,
   * before this flag existed, a portal nobody could sign in to and no
   * message saying why.
   */
  failed: boolean;
  /** Discard the current token and re-issue the challenge. */
  reset: () => void;
}

/**
 * @param action A short label recorded against the challenge, so that the
 *   analytics can tell an email step from a verify step.
 */
export function useTurnstile(action: string): Turnstile {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const widgetIdRef = useRef<string | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const container = containerRef.current;
    if (!container) return;

    void loadScript()
      .then(() => {
        if (cancelled || !window.turnstile || widgetIdRef.current !== null) return;
        widgetIdRef.current = window.turnstile.render(container, {
          sitekey: siteKey(),
          callback: (value: string) => {
            setFailed(false);
            setToken(value);
          },
          /*
           * Config errors (an invalid site key, a hostname the widget does
           * not allow) land here, not in the catch below: the script loaded
           * fine and render was called, and Turnstile reports the failure
           * through this callback. Without setFailed the only symptoms are
           * an absent widget and a disabled button.
           */
          'error-callback': () => {
            setToken(null);
            setFailed(true);
          },
          'expired-callback': () => setToken(null),
          'timeout-callback': () => setToken(null),
          theme: 'light',
          appearance: 'always',
          action,
        });
      })
      .catch(() => {
        /* Script failed to load, or render threw. Same visitor outcome. */
        setToken(null);
        setFailed(true);
      });

    return () => {
      cancelled = true;
      const widgetId = widgetIdRef.current;
      widgetIdRef.current = null;
      if (widgetId !== null && window.turnstile) {
        window.turnstile.remove(widgetId);
      }
    };
  }, [action]);

  const reset = useCallback(() => {
    setToken(null);
    const widgetId = widgetIdRef.current;
    if (widgetId !== null && window.turnstile) {
      window.turnstile.reset(widgetId);
    }
  }, []);

  return { containerRef, token, failed, reset };
}
